import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLImageFormats = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Images & Graphics</div>
                <h1>Image Formats (JPG, PNG, SVG, WebP)</h1>
                <p className="hero-lead">
                    Choose the right image format for every situation. Learn the technical differences,
                    compression methods, and use cases for JPEG, PNG, GIF, WebP, AVIF, and SVG.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Image Format Matters</h2>
                <p>
                    Choosing the right image format affects file size, loading speed, visual quality,
                    and user experience. Each format has specific strengths and ideal use cases.
                </p>

                <div className="impact-stats">
                    <div className="stat-card">
                        <h3>70%</h3>
                        <p>of average web page weight is images</p>
                    </div>
                    <div className="stat-card">
                        <h3>25-35%</h3>
                        <p>file size reduction with WebP vs JPEG</p>
                    </div>
                    <div className="stat-card">
                        <h3>50%</h3>
                        <p>smaller files with AVIF vs WebP</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>JPEG / JPG</h2>
                <p>
                    <strong>Joint Photographic Experts Group</strong> - Lossy compression format ideal for photographs.
                </p>

                <div className="format-details">
                    <h3>Technical Specifications:</h3>
                    <ul>
                        <li><strong>Extension:</strong> .jpg, .jpeg</li>
                        <li><strong>Compression:</strong> Lossy (DCT-based)</li>
                        <li><strong>Color Support:</strong> 16.7 million colors (24-bit)</li>
                        <li><strong>Transparency:</strong>  No</li>
                        <li><strong>Animation:</strong>  No</li>
                        <li><strong>Browser Support:</strong> Universal</li>
                    </ul>

                    <h3>How JPEG Compression Works:</h3>
                    <ol>
                        <li><strong>Color Space Conversion:</strong> RGB  YCbCr (luminance + chrominance)</li>
                        <li><strong>Chroma Subsampling:</strong> Reduces color information (human eye less sensitive)</li>
                        <li><strong>DCT Transform:</strong> Converts spatial data to frequency domain</li>
                        <li><strong>Quantization:</strong> Discards less important data (lossy step)</li>
                        <li><strong>Entropy Encoding:</strong> Huffman coding compresses remaining data</li>
                    </ol>

                    <h3>Best For:</h3>
                    <ul>
                        <li>Photographs</li>
                        <li>Complex images with gradients</li>
                        <li>Images with many colors</li>
                        <li>Product photos</li>
                        <li>Blog post images</li>
                    </ul>

                    <h3> Avoid For:</h3>
                    <ul>
                        <li>Text-heavy images (gets blurry)</li>
                        <li>Images requiring transparency</li>
                        <li>Logos and icons (artifacts appear)</li>
                        <li>Line drawings</li>
                        <li>Images with sharp edges</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>JPEG Usage</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Standard JPEG --&gt;
<img src="photo.jpg" alt="Landscape photo" width="1200" height="800">

<!-- Progressive JPEG (better perceived loading) --&gt;
<img src="photo-progressive.jpg" alt="Photo" loading="lazy">

<!-- Quality recommendations: --&gt;
<!-- - 80-90% for high-quality photos --&gt;
<!-- - 60-80% for general web use --&gt;
<!-- - 40-60% for thumbnails --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>PNG</h2>
                <p>
                    <strong>Portable Network Graphics</strong> - Lossless format with transparency support.
                </p>

                <div className="format-details">
                    <h3>Technical Specifications:</h3>
                    <ul>
                        <li><strong>Extension:</strong> .png</li>
                        <li><strong>Compression:</strong> Lossless (DEFLATE)</li>
                        <li><strong>Color Support:</strong> Up to 16.7 million + alpha (32-bit)</li>
                        <li><strong>Transparency:</strong> Yes (alpha channel)</li>
                        <li><strong>Animation:</strong>  No (APNG exists but limited support)</li>
                        <li><strong>Browser Support:</strong> Universal</li>
                    </ul>

                    <h3>PNG Variants:</h3>
                    <div className="variant-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Colors</th>
                                    <th>Use Case</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>PNG-8</td>
                                    <td>256 colors</td>
                                    <td>Simple graphics, small file size</td>
                                </tr>
                                <tr>
                                    <td>PNG-24</td>
                                    <td>16.7 million</td>
                                    <td>Full-color images, no transparency</td>
                                </tr>
                                <tr>
                                    <td>PNG-32</td>
                                    <td>16.7 million + alpha</td>
                                    <td>Full-color with transparency</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>Best For:</h3>
                    <ul>
                        <li>Logos and branding</li>
                        <li>Icons</li>
                        <li>Screenshots</li>
                        <li>Images requiring transparency</li>
                        <li>Text-heavy images</li>
                        <li>Line art and diagrams</li>
                    </ul>

                    <h3> Avoid For:</h3>
                    <ul>
                        <li>Photographs (large file size)</li>
                        <li>Complex images with gradients</li>
                        <li>When file size is critical</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>PNG Usage</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- PNG with transparency --&gt;
<img src="logo.png" alt="Company logo" width="200" height="100">

<!-- Optimize PNGs with tools like: --&gt;
<!-- - TinyPNG, ImageOptim, pngquant --&gt;
<!-- - Can reduce size by 50-80% without visible loss --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>WebP</h2>
                <p>
                    <strong>Google's modern format</strong> - Superior compression with transparency and animation support.
                </p>

                <div className="format-details">
                    <h3>Technical Specifications:</h3>
                    <ul>
                        <li><strong>Extension:</strong> .webp</li>
                        <li><strong>Compression:</strong> Both lossy and lossless</li>
                        <li><strong>Color Support:</strong> 16.7 million + alpha</li>
                        <li><strong>Transparency:</strong> Yes</li>
                        <li><strong>Animation:</strong> Yes</li>
                        <li><strong>Browser Support:</strong> 95%+ (Chrome 32+, Firefox 65+, Edge 18+, Safari 14+)</li>
                    </ul>

                    <h3>Why WebP is Superior:</h3>
                    <ul>
                        <li>25-35% smaller than JPEG at same quality</li>
                        <li>26% smaller than PNG for lossless images</li>
                        <li>Supports transparency (unlike JPEG)</li>
                        <li>Supports animation (unlike PNG)</li>
                        <li>Faster loading times</li>
                    </ul>

                    <h3>Best For:</h3>
                    <ul>
                        <li>All use cases (photos, graphics, logos)</li>
                        <li>Modern websites targeting performance</li>
                        <li>Progressive web apps</li>
                        <li>Mobile-first sites</li>
                    </ul>

                    <h3> Considerations:</h3>
                    <ul>
                        <li>Always provide JPEG/PNG fallback for older browsers</li>
                        <li>Limited support in older iOS (pre-14)</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>WebP with Fallback</span>
                    </div>
                    <CodeBlock language="html" code={`<picture>
    <!-- Try WebP first (modern browsers) --&gt;
    <source type="image/webp" srcset="image.webp">
    
    <!-- Fallback to JPEG (older browsers) --&gt;
    <img src="image.jpg" alt="Product photo" width="800" height="600">
</picture>

<!-- Or serve WebP conditionally from server based on Accept header --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>AVIF</h2>
                <p>
                    <strong>AV1 Image File Format</strong> - Next-generation format with best compression.
                </p>

                <div className="format-details">
                    <h3>Technical Specifications:</h3>
                    <ul>
                        <li><strong>Extension:</strong> .avif</li>
                        <li><strong>Compression:</strong> Both lossy and lossless (AV1 codec)</li>
                        <li><strong>Color Support:</strong> Up to 4.3 billion colors (HDR support)</li>
                        <li><strong>Transparency:</strong> Yes</li>
                        <li><strong>Animation:</strong> Yes</li>
                        <li><strong>Browser Support:</strong> Chrome 85+, Firefox 93+, Safari 16+ (~80%)</li>
                    </ul>

                    <h3>Why AVIF is the Future:</h3>
                    <ul>
                        <li>50% smaller than WebP at same quality</li>
                        <li>HDR and wide color gamut support</li>
                        <li>Better detail preservation</li>
                        <li>Open-source and royalty-free</li>
                    </ul>

                    <h3>Best For:</h3>
                    <ul>
                        <li>Cutting-edge websites</li>
                        <li>Maximum performance optimization</li>
                        <li>High-quality images at small sizes</li>
                        <li>HDR content</li>
                    </ul>

                    <h3> Limitations:</h3>
                    <ul>
                        <li>Encoding is slow (use for static content)</li>
                        <li>Not supported in older browsers</li>
                        <li>Always need fallbacks</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Progressive Enhancement: AVIF  WebP  JPEG</span>
                    </div>
                    <CodeBlock language="html" code={`<picture>
    <!-- Try AVIF first (best compression) --&gt;
    <source type="image/avif" srcset="image.avif">
    
    <!-- Try WebP (good compression, better support) --&gt;
    <source type="image/webp" srcset="image.webp">
    
    <!-- Fallback to JPEG (universal) --&gt;
    <img src="image.jpg" alt="Photo" width="1200" height="800">
</picture>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>SVG</h2>
                <p>
                    <strong>Scalable Vector Graphics</strong> - XML-based vector format, resolution-independent.
                </p>

                <div className="format-details">
                    <h3>Technical Specifications:</h3>
                    <ul>
                        <li><strong>Extension:</strong> .svg</li>
                        <li><strong>Format:</strong> Vector (XML-based)</li>
                        <li><strong>Scalability:</strong> Infinite (no pixelation)</li>
                        <li><strong>File Size:</strong> Usually very small</li>
                        <li><strong>Editing:</strong> Can edit with text editor</li>
                        <li><strong>CSS/JS:</strong> Can style and animate</li>
                        <li><strong>Browser Support:</strong> Universal</li>
                    </ul>

                    <h3>Best For:</h3>
                    <ul>
                        <li>Logos and icons</li>
                        <li>Illustrations</li>
                        <li>Charts and graphs</li>
                        <li>Responsive designs (scales perfectly)</li>
                        <li>Animations</li>
                    </ul>

                    <h3> Not For:</h3>
                    <ul>
                        <li>Photographs</li>
                        <li>Complex images with many colors</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>SVG Usage</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- External SVG --&gt;
<img src="logo.svg" alt="Logo" width="200" height="100">

<!-- Inline SVG (can style with CSS) --&gt;
<svg width="100" height="100" viewBox="0 0 100 100">
    <circle cx="50" cy="50" r="40" fill="#007bff"/>
    <text x="50" y="55" text-anchor="middle" fill="white" font-size="20">SVG</text>
</svg>

<!-- SVG as background (CSS) --&gt;
<style>
.icon {
    width: 24px;
    height: 24px;
    background-image: url('icon.svg');
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>GIF</h2>
                <p>
                    <strong>Graphics Interchange Format</strong> - Legacy format supporting animation.
                </p>

                <div className="format-details">
                    <h3>Technical Specifications:</h3>
                    <ul>
                        <li><strong>Extension:</strong> .gif</li>
                        <li><strong>Colors:</strong> Maximum 256 colors (8-bit)</li>
                        <li><strong>Transparency:</strong> Yes (binary, not alpha)</li>
                        <li><strong>Animation:</strong> Yes</li>
                        <li><strong>Compression:</strong> Lossless (LZW)</li>
                        <li><strong>Browser Support:</strong> Universal</li>
                    </ul>

                    <h3>Use For:</h3>
                    <ul>
                        <li>Simple animations (prefer video/WebP now)</li>
                        <li>Legacy support</li>
                    </ul>

                    <h3> Better Alternatives:</h3>
                    <ul>
                        <li><strong>For animations:</strong> Use MP4 video or WebP animation (much smaller)</li>
                        <li><strong>For graphics:</strong> Use PNG (better quality)</li>
                        <li><strong>For photos:</strong> Use JPEG/WebP</li>
                    </ul>
                </div>

                <Warning><strong> GIF is Outdated:</strong> Modern alternatives (WebP animation, MP4 video)
                    provide better quality and much smaller file sizes. Use GIF only for legacy support.</Warning>
            </div>

            <div className="content-section">
                <h2>Format Comparison Table</h2>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Format</th>
                                <th>Type</th>
                                <th>Transparency</th>
                                <th>Animation</th>
                                <th>Best For</th>
                                <th>File Size</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>JPEG</strong></td>
                                <td>Raster (Lossy)</td>
                                <td></td>
                                <td></td>
                                <td>Photos</td>
                                <td>Small</td>
                            </tr>
                            <tr>
                                <td><strong>PNG</strong></td>
                                <td>Raster (Lossless)</td>
                                <td></td>
                                <td></td>
                                <td>Graphics, Logos</td>
                                <td>Large</td>
                            </tr>
                            <tr>
                                <td><strong>WebP</strong></td>
                                <td>Raster (Both)</td>
                                <td></td>
                                <td></td>
                                <td>Everything</td>
                                <td>Very Small</td>
                            </tr>
                            <tr>
                                <td><strong>AVIF</strong></td>
                                <td>Raster (Both)</td>
                                <td></td>
                                <td></td>
                                <td>Everything</td>
                                <td>Smallest</td>
                            </tr>
                            <tr>
                                <td><strong>SVG</strong></td>
                                <td>Vector</td>
                                <td></td>
                                <td></td>
                                <td>Icons, Logos</td>
                                <td>Tiny</td>
                            </tr>
                            <tr>
                                <td><strong>GIF</strong></td>
                                <td>Raster (Lossless)</td>
                                <td></td>
                                <td></td>
                                <td>Legacy</td>
                                <td>Large</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Choosing the Right Format</h2>
                <div className="decision-flowchart">
                    <h3>Quick Decision Guide:</h3>
                    <ol>
                        <li><strong>Is it a vector graphic (logo/icon)?</strong>  Use SVG</li>
                        <li><strong>Is it a photograph?</strong>
                            <ul>
                                <li>Modern browser?  AVIF &gt; WebP &gt; JPEG</li>
                                <li>Universal support needed?  JPEG</li>
                            </ul>
                        </li>
                        <li><strong>Need transparency?</strong>
                            <ul>
                                <li>Modern browser?  WebP &gt; PNG</li>
                                <li>Universal support?  PNG</li>
                            </ul>
                        </li>
                        <li><strong>Simple animation?</strong>
                            <ul>
                                <li>Best: Short MP4 video (smaller than GIF)</li>
                                <li>Alternative: WebP animation</li>
                                <li>Legacy: GIF (last resort)</li>
                            </ul>
                        </li>
                    </ol>
                </div>
            </div>

            <div className="content-section">
                <h2>Practical Implementation</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Complete Progressive Enhancement</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Photos: AVIF &gt; WebP &gt; JPEG --&gt;
<picture>
    <source type="image/avif" srcset="photo.avif">
    <source type="image/webp" srcset="photo.webp">
    <img src="photo.jpg" alt="Photo" width="1200" height="800" loading="lazy">
</picture>

<!-- Graphics with transparency: WebP &gt; PNG --&gt;
<picture>
    <source type="image/webp" srcset="logo.webp">
    <img src="logo.png" alt="Logo" width="200" height="100">
</picture>

<!-- Icons: SVG (vector, tiny size) --&gt;
<img src="icon.svg" alt="Icon" width="24" height="24">

<!-- Animation: MP4 video (not GIF) --&gt;
<video autoplay loop muted playsinline width="400" height="300">
    <source src="animation.mp4" type="video/mp4">
    <source src="animation.webm" type="video/webm">
</video>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Conversion Tools</h2>
                <ul>
                    <li><strong>Online:</strong> Squoosh.app (Google), TinyPNG, CloudConvert</li>
                    <li><strong>CLI Tools:</strong> ImageMagick, cwebp, avifenc, svgo</li>
                    <li><strong>Build Tools:</strong> imagemin, sharp (Node.js)</li>
                    <li><strong>CDN:</strong> Cloudinary, Imgix (automatic format conversion)</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use WebP/AVIF for modern browsers</li>
                            <li>Provide fallbacks for older browsers</li>
                            <li>Use SVG for logos and icons</li>
                            <li>Compress all images before upload</li>
                            <li>Choose format based on content type</li>
                            <li>Test file sizes for each format</li>
                            <li>Use progressive JPEG for photos</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use PNG for photographs</li>
                            <li>Use JPEG for logos/icons</li>
                            <li>Use GIF for animations (use video)</li>
                            <li>Serve only WebP without fallback</li>
                            <li>Skip image compression</li>
                            <li>Use wrong format for content type</li>
                            <li>Ignore browser support</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLImageFormats;



